import { useEffect, useRef, useState } from 'react';

/**
 * Click-and-drag to pan a scrollable container. Used by every Gantt view and,
 * since 2026-09-27, by every page-scroll table (`OrderTableShell`,
 * `gags/GagGroupTable`, `budget/CrewMembersTableFrame`) via `verticalPansWindow`.
 *
 * @param {React.RefObject<HTMLElement>} scrollRef  ref to the scrollable container
 * @param {object} [options]
 * @param {'x'|'y'|'both'} [options.axis='both']   which axes to pan on
 * @param {string} [options.syncLabelsId]          id of a sibling element whose
 *   scrollTop should mirror the container's during a pan (e.g. a sticky labels
 *   column to the left of the timeline). The container's onScroll handler
 *   should already sync this for wheel/scrollbar use; we sync it from the pan
 *   too because programmatic scrollTop changes don't reliably fire scroll
 *   events in time for the labels to keep up.
 *
 *   🚨 THE MIRROR IS 1:1 AND MUST STAY 1:1. THERE IS NO OFFSET OPTION, AND A
 *   `syncLabelsOffset` WAS ADDED HERE ON 2026-09-22 AND REVERTED THE SAME DAY
 *   — it subtracted the caller's header height, and it BROKE a working screen
 *   (the Breakdown timeline; the owner caught it in a browser).
 *
 *   The argument for it was that the two elements are in different coordinate
 *   spaces, "verified structurally": the date-axis block sits INSIDE the
 *   scrolled timeline pane while the label column's heading is a
 *   `flex-shrink-0` sibling OUTSIDE the scrolled labels div. **Both premises
 *   are TRUE and the conclusion was still wrong.** The axis is a CONTENT
 *   offset inside the chart's scroll content; the heading is a BOX offset that
 *   starts the label pane that same distance lower ON SCREEN. They point
 *   opposite ways and CANCEL, so a raw mirror is exactly right. Independent
 *   check: the two panes' maximum scroll ranges are provably EQUAL, and only a
 *   1:1 map preserves a range — subtracting the header makes the last
 *   header-height of the label list permanently unreachable.
 *
 *   🥇 A DEFECT CLAIM NEEDS THE SAME PROOF AS A FIX. That claim propagated
 *   through a duplication scanner, a manifest reason, this docblock, a
 *   rewritten test and two briefs without one independent check. *"Verified
 *   structurally"* is not *"verified"*: a structural argument can be
 *   confidently wrong by assuming two coordinate frames share an origin they
 *   do not. The full geometry, with the algebra, is in
 *   `gags/GagTimelineView.js` at `rowTopToScrollTop`'s docblock.
 *   🚫 Do not re-add an offset option "for the caller that needs it". All
 *   three Gantt views have the SAME symmetric shape and all three want 1:1.
 *   🔑 The caller's own `onScroll` must write the SAME number this does, or
 *   the pan path and the wheel path race.
 *
 * @param {React.RefObject<HTMLElement>} [options.scrollTargetRef] — pan THIS
 *   element instead of `scrollRef`'s, while still listening for the press on
 *   `scrollRef`'s. Added 2026-09-23 for the Crew Gantt's sticky date band: the
 *   band sits OUTSIDE the timeline (a sticky child of an x-only scroller is
 *   inert), so a press on it never reaches the timeline's own listener; the
 *   band passes the timeline as its target and a drag on the dates pans the
 *   chart sideways exactly as a drag on the rows does. Omitted, behaviour is
 *   unchanged: the pressed element is the scrolled element.
 *
 * @param {boolean} [options.verticalPansWindow=false] — the PAGE-SCROLL TABLE
 *   mode (2026-09-27, owner: *"click and drag … sideways and up and down like
 *   gannt"*). A vertical pan scrolls the WINDOW, and a horizontal pan scrolls
 *   the `[data-page-scroll-pane]` the press started in (resolved at PRESS time —
 *   the Breakdown has one rows pane per group; `useLinkedScrollLeft` moves the
 *   rest), falling back to `scrollTargetRef`. Listen on the table's outer box.
 *   🪤 It reads `clientX/Y`, never `pageX/Y`: `pageY` moves WITH a window
 *   scroll, so a pan that scrolls the page would feed its own delta back.
 *   Also refuses a press on the target's own visible scrollbar gutter.
 *
 *   🚨 **A NESTED VERTICAL SCROLLER OWNS ITS OWN GESTURE (fix round,
 *   2026-09-27).** A press inside any element between the target and the
 *   listened box that scrolls vertically ITSELF — computed `overflow-y`
 *   `auto`/`scroll` AND `scrollHeight > clientHeight` — never starts a pan.
 *   Found by the scoped review with a real mouse: a 60px drag inside a
 *   collapsed order row's item-preview mini-list (`OrderTableView`'s
 *   `ItemPreview`, `data-scrollable="true"`) moved `window.scrollY` by 60 and
 *   the list's own `scrollTop` by 0. 🪤 The gutter guard above could NEVER
 *   have caught it: that list hides its native bar
 *   (`PREVIEW_NO_NATIVE_SCROLLBAR`), so there is no gutter to press. The rule
 *   is GENERIC, not a `data-pan-skip` on that one box, and deliberately so: a
 *   mark would also kill pans on a preview that fits without overflowing, and
 *   it would hide this rule from its own mutation proof. A press on the ROW
 *   beside the list still pans.
 *
 * ── A PAN THAT NEVER SEES ITS MOUSEUP IS ABORTED, NOT LEFT LIVE (fix round,
 *    2026-09-27) ────────────────────────────────────────────────────────────
 * `mouseup` only reaches `window` if the window still has the pointer. Alt-Tab
 * away mid-drag, or hide the tab, and it never arrives — so `html[data-drag-
 * panning]` and the global `grabbing` cursor stayed on app-wide until the next
 * full pan (reproduced by the review with `window.dispatchEvent(new
 * Event('blur'))` mid-drag). `window` `blur` and `document` `visibilitychange`
 * (hidden) now run the same cleanup as mouseup — cursor, `userSelect`,
 * `PANNING_ATTR`, the press — but an ABORTED pan ARMS NO swallower and
 * DISARMS any armed one, so no later click can ever be eaten.
 *
 * Skips children matching `PAN_SKIP_SELECTOR` (`button, a, select, input,
 * textarea, [data-drag-handle], [data-pan-skip]`) so interactive elements + bar
 * drag handles still work normally. To opt a bar out of pan-on-drag, add
 * `data-pan-skip` to its wrapper. Primary button only.
 *
 * ── A PRESS IS A CLICK UNTIL IT MOVES `PAN_THRESHOLD_PX` (2026-09-27) ──────
 * Nothing happens on mousedown: `grabbing` and `userSelect: none` land only
 * once the pointer has moved MORE than the threshold on either axis, so a
 * press-and-release is an ordinary click. 🚨 **AND A REAL PAN SWALLOWS THE
 * CLICK THAT FOLLOWS IT** — a mousedown and a mouseup inside one row fire a
 * `click` on that row however far the pointer travelled (browser-measured on
 * Department Orders before this change: a 60px drag ending on its own row
 * EXPANDED it), so every pan would otherwise expand a table row or open a
 * Gantt bar (`teams/LabourGanttView`'s bars carry no `data-pan-skip`). A
 * one-shot capture-phase `click` listener on `window` eats it and removes
 * itself; `PAN_CLICK_SWALLOW_FALLBACK_MS` removes it if no click comes (a
 * release outside the window). `gags/GagTimelineView`'s local
 * `PAN_CLICK_SUPPRESS_PX` predates this and is now redundant, not wrong.
 *
 * ── 🚨 THE ELEMENT IS TRACKED IN STATE, AND THAT IS A FIX, NOT A STYLE ───
 * Fixed 2026-09-22, after the product owner reported drag-panning DEAD on
 * the Breakdown timeline twice. The attach effect used to open
 * `const el = scrollRef.current; if (!el) return;` and was keyed on
 * `[scrollRef, axis, syncLabelsId]` — **a ref OBJECT and two string
 * literals, none of which can ever change.** So the effect ran exactly once,
 * on the first render, and if the scroller was not in the DOM yet it bailed
 * and was NEVER RE-RUN: the listener was never attached at all.
 *
 * 🔑 **A `ref.current` IS NOT A REACTIVE VALUE — an effect keyed on the ref
 * cannot see the element arrive.** The only moment a ref's new value is
 * readable is after a commit, so the tracking effect below deliberately has
 * **no dependency array**: it runs after every render and promotes
 * `scrollRef.current` into state, which re-runs the real effect exactly once,
 * with the element. The functional `setEl` bails out on an unchanged value
 * (`Object.is`), so this costs one extra commit on mount and nothing
 * thereafter.
 *
 * 🪤 **THE TRAP THAT REMAINS, AND IT IS THE CALLER'S:** this fixes PANNING
 * for a component that renders its scroller behind an early return — it does
 * NOT make the early return harmless. Any OTHER effect in that component
 * keyed on the same ref has exactly this bug, and nothing here helps it.
 * `components/gags/GagTimelineView.js` is the one that bit: it owns its own
 * fetch, so `loading` starts `true` and its first render is ALWAYS the
 * spinner. Its `error` and empty-filter returns arm the same trap. The other
 * four callers were handed resolved data by a parent, which is the only
 * reason they worked — **their empty-state early returns are the same loaded
 * gun, and an ordinary refactor that makes a parent pass `[]` first fires
 * it.** The alternative fix was to move each caller's scroller out from
 * behind its early return; it was rejected because it closes one instance
 * and leaves five.
 *
 * 🚫 Do not "simplify" the tracking effect by giving it a dependency array.
 * Any array at all — including `[scrollRef]` — restores the original defect,
 * and `client/src/components/gags/GagTimelineView.dragPan.test.js` turns red.
 */
/** How far (px, either axis) a press may travel and still be a click. */
export const PAN_THRESHOLD_PX = 5;
/** How long the post-pan click swallower waits for its click before giving up. */
export const PAN_CLICK_SWALLOW_FALLBACK_MS = 100;
/** A press on any of these never starts a pan. Prefer adding `data-pan-skip`. */
export const PAN_SKIP_SELECTOR = 'button, a, select, input, textarea, [data-drag-handle], [data-pan-skip]';
/** The x-scroll panes a `verticalPansWindow` pan resolves its horizontal target from. */
export const PAGE_SCROLL_PANE_SELECTOR = '[data-page-scroll-pane]';
/** Set on `<html>` while a pan is live; `index.css` turns every cursor to `grabbing`. */
export const PANNING_ATTR = 'data-drag-panning';

// Did this press land on the target's own (visible) scrollbar gutter?
function pressedOwnScrollbar(e) {
  const t = e.target;
  if (!t || typeof e.offsetX !== 'number' || !(t.clientWidth > 0)) return false;
  return e.offsetX >= t.clientWidth || e.offsetY >= t.clientHeight;
}

// Did this press land inside an element, between the target and the listened
// box, that scrolls VERTICALLY on its own? That box owns the vertical gesture
// (a wheel scrolls it), so a window pan from inside it fights it. See the
// docblock's nested-scroller block.
function pressedInNestedScroller(e, box) {
  for (let n = e.target; n && n !== box; n = n.parentElement) {
    if (n.nodeType !== 1) continue;
    const oy = window.getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return true;
  }
  return false;
}

// Eat the ONE click a completed pan is about to produce. See the docblock.
// Returns a disarm function so an aborted pan can take the swallower back.
function swallowNextClick() {
  let timer = null;
  const swallow = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
    done();
  };
  function done() {
    window.removeEventListener('click', swallow, true);
    clearTimeout(timer);
  }
  window.addEventListener('click', swallow, true);
  timer = setTimeout(done, PAN_CLICK_SWALLOW_FALLBACK_MS);
  return done;
}

export default function useDragScroll(scrollRef, options = {}) {
  const { axis = 'both', syncLabelsId, scrollTargetRef, verticalPansWindow = false } = options;
  // The live press, or null: `{ x, y, target, left, top, panning }`. The target
  // is resolved at PRESS time, so a target that mounts late (or is swapped, or
  // is one of several group panes) is read when it matters, not at attach time.
  const press = useRef(null);
  // The armed post-pan click swallower's disarm, or null.
  const disarmSwallow = useRef(null);

  // The scroller, as a REACTIVE value. See the docblock — no dependency
  // array is deliberate and is the whole fix.
  const [el, setEl] = useState(null);
  useEffect(() => {
    setEl((current) => (scrollRef.current === current ? current : scrollRef.current));
  });

  useEffect(() => {
    if (!el) return;

    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      if (e.target.closest(PAN_SKIP_SELECTOR)) return;
      let target;
      if (verticalPansWindow) {
        if (pressedOwnScrollbar(e)) return;
        if (pressedInNestedScroller(e, el)) return;
        const pane = e.target.closest(PAGE_SCROLL_PANE_SELECTOR);
        target = pane && el.contains(pane) ? pane : (scrollTargetRef ? scrollTargetRef.current : null);
      } else {
        target = scrollTargetRef ? scrollTargetRef.current : el;
        if (!target) return;
      }
      press.current = {
        x: e.clientX,
        y: e.clientY,
        target,
        left: target ? target.scrollLeft : 0,
        top: verticalPansWindow ? window.scrollY : target.scrollTop,
        panning: false,
      };
    };

    const onMouseMove = (e) => {
      const p = press.current;
      if (!p) return;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      if (!p.panning) {
        if (Math.abs(dx) <= PAN_THRESHOLD_PX && Math.abs(dy) <= PAN_THRESHOLD_PX) return;
        p.panning = true;
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
        document.documentElement.setAttribute(PANNING_ATTR, '');
        // The first few px before the threshold may have begun a selection.
        const sel = window.getSelection && window.getSelection();
        if (sel && sel.removeAllRanges) sel.removeAllRanges();
      }
      if (axis !== 'y' && p.target) {
        p.target.scrollLeft = p.left - dx;
      }
      if (axis !== 'x') {
        if (verticalPansWindow) {
          window.scrollTo({ left: window.scrollX, top: p.top - dy, behavior: 'instant' });
        } else {
          p.target.scrollTop = p.top - dy;
          if (syncLabelsId) {
            const labels = document.getElementById(syncLabelsId);
            // 🚨 A BARE 1:1 COPY. No offset — see the docblock above.
            if (labels) labels.scrollTop = p.target.scrollTop;
          }
        }
      }
    };

    // End the press. A COMPLETED pan (a real mouseup) arms the click
    // swallower; an ABORTED one (blur, hidden tab, unmount) disarms any armed
    // swallower instead, so no later click can be eaten.
    const endPress = (completed) => {
      const p = press.current;
      press.current = null;
      if (!completed && disarmSwallow.current) {
        disarmSwallow.current();
        disarmSwallow.current = null;
      }
      if (!p || !p.panning) return;
      el.style.cursor = '';
      el.style.userSelect = '';
      document.documentElement.removeAttribute(PANNING_ATTR);
      if (completed) disarmSwallow.current = swallowNextClick();
    };
    const onMouseUp = () => endPress(true);
    const onAbort = () => endPress(false);
    const onVisibility = () => { if (document.visibilityState === 'hidden') endPress(false); };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('blur', onAbort);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      // Unmounted mid-pan: never leave the whole page wearing `grabbing`.
      endPress(false);
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('blur', onAbort);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [el, axis, syncLabelsId, scrollTargetRef, verticalPansWindow]);
}
